import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GuidedTourDirective } from '../../guided-tour.directive';
import { GuidedTourService } from '../../../service/guided-tour.service';
import { OA_ADMIN_AREA } from '../../../model/guided-tour';
import { Router } from '@angular/router';
import { MarkantElementsModule } from '@markant/elements-angular';
import { firstValueFrom } from 'rxjs';
import { GroupAssignmentComponent } from '@markant-platform/dashboard/feature-dashboard-group-assignment';
import { SectionFinishDialogComponent } from '../../../section-finish-dialog/section-finish-dialog.component';
import { GuidedTourStep } from '../../../model/guided-tour-step';
import { GuidedTourWrapperComponent } from '../../guided-tour-wrapper.component';
import { GuidedTourHighlightDialogComponent } from '../../highlight-dialog/guided-tour-highlight-dialog.component';
import { GroupAssignmentDemoComponent } from './group-assignment-demo.component';
import { TranslocoModule } from '@jsverse/transloco';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { HighlightDialogConfig } from '../../../model/highlight-dialog';

@Component({
  selector: 'markant-platform-my-groups',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    GroupAssignmentComponent,
    SectionFinishDialogComponent,
    GuidedTourWrapperComponent,
    GuidedTourHighlightDialogComponent,
    GroupAssignmentDemoComponent,
    TranslocoModule,
    ModalBridgeComponent,
  ],
  templateUrl: './my-groups.component.html',
  styleUrls: ['./my-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyGroupsComponent extends GuidedTourDirective {
  @ViewChild('guidedTourElement') guidedTourElement!: GuidedTourWrapperComponent;

  enableClickThrough = true;

  steps: GuidedTourStep[] = [
    {
      highlightTargetId: 'groups-tile-content',
      clickTargetId: 'groups',
      description: { text: 'guidedTour.tours.guided-tour-oa-admin-area.section5.description.step1' },
      hideArrow: true,
    },
    {
      highlightTargetId: 'markant-body',
      shape: 'rectangle',
      ignoreHighlightClick: true,
      hideBackButton: true,
      nextOnOverlayClick: true,
    },
    {
      highlightTargetId: 'group-assignment-overview-title',
      nextOnOverlayClick: true,
      description: { text: 'guidedTour.tours.guided-tour-oa-admin-area.section5.description.step3' },
      border: 15,
    },
    {
      highlightTargetId: 'my-groups-tour-step4-dialog',
      description: { text: 'guidedTour.tours.guided-tour-oa-admin-area.section5.description.step4' },
      shape: 'rectangle',
      ignoreHighlightClick: true,
    },
    {
      highlightTargetId: 'open-dialog-create-group-button',
      description: {
        text: 'guidedTour.tours.guided-tour-oa-admin-area.section5.description.step5',
        buttons: [
          {
            id: 'tour-finish-button',
            text: 'guidedTour.tours.guided-tour-oa-admin-area.section5.step5.endTourButton',
            colorScheme: 'outlined',
            callback: this.showEndDialog.bind(this),
          },
          {
            id: 'tour-create-group-button',
            text: 'guidedTour.tours.guided-tour-oa-admin-area.section5.step5.createGroupButton',
            icon: {
              position: 'trailing',
              id: 'm_arrow-right-small',
            },
            callback: this.openCreateGroup.bind(this),
          },
        ],
      },
      border: 15,
    },
  ];

  dialogConfig: HighlightDialogConfig = {
    position: {
      position: 'absolute',
      vertical: {
        from: 'top',
        amount: '16px',
      },
    },
  };

  private stepDelay?: number;

  backButtonCallback = this.previousTarget.bind(this);
  nextButtonCallback = this.nextTarget.bind(this);

  constructor(
    guidedService: GuidedTourService,
    protected override router: Router
  ) {
    super(OA_ADMIN_AREA, 5, guidedService, router);
  }

  protected override async cancelSection(): Promise<void> {
    await this.guidedTourElement.end();
    super.cancelSection();
  }

  protected override async nextTarget(): Promise<void> {
    super.nextTarget();
    this.enableClickThrough = true;

    switch (this.currentStep) {
      case 1:
        await this.navigateToAdminDashboard();
        await this.guidedTourElement.start();
        break;
      case 2:
        await this.guidedTourElement.next();
        this.stepDelay = window.setTimeout(async () => await this.nextTarget(), 3000);
        break;
      case 3:
        this.cleanTimeoutAndFocus(this.stepDelay);
        this.enableClickThrough = false;
        await this.guidedTourElement.next();
        break;
      case 4:
        await this.guidedTourElement.next();
        break;
      case 5:
        await this.guidedTourElement.next();
        this.enableClickThrough = false;
        break;
    }
  }

  protected override async previousTarget(): Promise<void> {
    if (this.currentStep === 3) {
      await this.jumpToTarget(this.currentStep - 2);
    } else {
      await this.jumpToTarget(this.currentStep - 1);
    }
  }

  protected override async jumpToTarget(target: number): Promise<void> {
    if (target < 1) {
      await this.router.navigate(['']);
      await this.cancelSection();
      return;
    }
    super.jumpToTarget(target);
    this.enableClickThrough = true;

    if (this.currentStep === 1) {
      await this.navigateToAdminDashboard();
    }

    await this.guidedTourElement.jumpTo(target - 1);
  }

  private async navigateToAdminDashboard(): Promise<void> {
    await this.router.navigate(['/organization']);
    await firstValueFrom(this.router.events);
  }

  async backToDashboard(): Promise<void> {
    await this.guidedTourElement.end();
    await this.router.navigate(['']);
    await this.completeSection(false);
  }

  async openCreateGroup(): Promise<void> {
    await this.guidedTourElement.end();
    (<HTMLDivElement>document.querySelector('#open-dialog-create-group-button')).dispatchEvent(new MouseEvent('click'));
    await this.completeSection(false);
  }

  async showEndDialog(): Promise<void> {
    await this.guidedTourService.completeSection(this.guidedTour.id, this.sectionNumber, true);
    await this.guidedTourElement.end();
  }
}
